/**
 * Deep Links Handler for Capacitor
 * Handles app URL scheme deep linking
 */

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { isNativePlatform } from './platform';

export interface DeepLinkRoute {
  path: string;
  handler: (params: Record<string, string>, fullUrl: string) => void;
}

let registeredRoutes: DeepLinkRoute[] = [];
let isListening = false;
let defaultHandler: ((url: string) => void) | null = null;

/**
 * Initialize deep link handling
 */
export async function initializeDeepLinks(
  routes: DeepLinkRoute[],
  onUnhandledLink?: (url: string) => void,
): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  if (isListening) {
    console.log('Deep links already initialized');
    return;
  }

  registeredRoutes = routes;
  defaultHandler = onUnhandledLink || null;

  // Listen for app URL open events
  await App.addListener('appUrlOpen', handleDeepLink);

  isListening = true;
  console.log('Deep links initialized with', routes.length, 'routes');
}

/**
 * Handle incoming deep link
 */
function handleDeepLink(event: URLOpenListenerEvent): void {
  console.log('Deep link received:', event.url);

  try {
    const url = new URL(event.url);
    const pathname = url.pathname;
    const params = Object.fromEntries(url.searchParams.entries());

    // Try to match a registered route
    for (const route of registeredRoutes) {
      const match = matchRoute(route.path, pathname);
      if (match) {
        const allParams = { ...params, ...match };
        route.handler(allParams, event.url);
        return;
      }
    }

    // No route matched, call default handler
    defaultHandler?.(event.url);
  } catch (error) {
    console.error('Error handling deep link:', error);
    defaultHandler?.(event.url);
  }
}

/**
 * Match a route pattern against a pathname
 * Supports :param syntax for dynamic segments
 */
function matchRoute(pattern: string, pathname: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);

  if (patternParts.length !== pathParts.length) {
    return null;
  }

  const params: Record<string, string> = {};

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];

    if (patternPart.startsWith(':')) {
      // Dynamic parameter
      const paramName = patternPart.slice(1);
      params[paramName] = pathPart;
    } else if (patternPart !== pathPart) {
      // Static segment doesn't match
      return null;
    }
  }

  return params;
}

/**
 * Get common deep link routes for InsuraCentral
 * Routes aligned with actual app structure in /app/(authenticated)/
 */
export function getInsuraCentralRoutes(router: { push: (path: string) => void }): DeepLinkRoute[] {
  return [
    // Lead deep links - /app/(authenticated)/leads/[id]/page.tsx
    {
      path: '/leads/:leadId',
      handler: (params) => {
        router.push(`/leads/${params.leadId}`);
      },
    },
    {
      path: '/lead/:leadId',
      handler: (params) => {
        router.push(`/leads/${params.leadId}`);
      },
    },

    // Leads list - /app/(authenticated)/leads/page.tsx
    {
      path: '/leads',
      handler: () => {
        router.push('/leads');
      },
    },

    // Appointment deep links - /app/(authenticated)/appointments/[id]/page.tsx
    {
      path: '/appointments/:appointmentId',
      handler: (params) => {
        router.push(`/appointments/${params.appointmentId}`);
      },
    },
    {
      path: '/appointment/:appointmentId',
      handler: (params) => {
        router.push(`/appointments/${params.appointmentId}`);
      },
    },

    // Appointments list - /app/(authenticated)/appointments/page.tsx
    {
      path: '/appointments',
      handler: () => {
        router.push('/appointments');
      },
    },

    // Reminder deep links - /app/(authenticated)/reminders/page.tsx
    // Note: Reminders page exists but no individual reminder route
    {
      path: '/reminders/:reminderId',
      handler: (params) => {
        router.push(`/reminders?highlight=${params.reminderId}`);
      },
    },
    {
      path: '/reminders',
      handler: () => {
        router.push('/reminders');
      },
    },

    // Call/Dialer deep links - /app/(authenticated)/dialer/page.tsx
    {
      path: '/call/:callSid',
      handler: (params) => {
        router.push(`/dialer?incoming=${params.callSid}`);
      },
    },
    {
      path: '/dialer',
      handler: (params, fullUrl) => {
        const url = new URL(fullUrl);
        router.push(`/dialer${url.search}`);
      },
    },

    // Communication deep links - /app/(authenticated)/communication/page.tsx
    {
      path: '/communication',
      handler: () => {
        router.push('/communication');
      },
    },
    {
      path: '/messages/:conversationId',
      handler: (params) => {
        router.push(`/communication?conversation=${params.conversationId}`);
      },
    },
    {
      path: '/sms/:leadId',
      handler: (params) => {
        router.push(`/leads/${params.leadId}?tab=messages`);
      },
    },

    // Dashboard - /app/(authenticated)/dashboard/page.tsx
    {
      path: '/dashboard',
      handler: () => {
        router.push('/dashboard');
      },
    },

    // Profile - /app/(authenticated)/profile/page.tsx
    {
      path: '/profile',
      handler: () => {
        router.push('/profile');
      },
    },

    // Settings - /app/(authenticated)/settings/page.tsx
    {
      path: '/settings',
      handler: () => {
        router.push('/settings');
      },
    },
    {
      path: '/settings/phone-numbers',
      handler: () => {
        router.push('/settings/phone-numbers');
      },
    },
    {
      path: '/settings/a2p-verification',
      handler: () => {
        router.push('/settings/a2p-verification');
      },
    },

    // Agency Dashboard - /app/(authenticated)/agency-dashboard/page.tsx
    {
      path: '/agency-dashboard',
      handler: () => {
        router.push('/agency-dashboard');
      },
    },

    // Admin Panel - /app/(authenticated)/admin-panel/page.tsx
    {
      path: '/admin-panel',
      handler: () => {
        router.push('/admin-panel');
      },
    },

    // Compliance - /app/(authenticated)/compliance/page.tsx
    {
      path: '/compliance',
      handler: () => {
        router.push('/compliance');
      },
    },
    {
      path: '/compliance/frameworks/:id',
      handler: (params) => {
        router.push(`/compliance/frameworks/${params.id}`);
      },
    },

    // Resources - /app/(authenticated)/resources/page.tsx
    {
      path: '/resources',
      handler: () => {
        router.push('/resources');
      },
    },

    // Sentimentals - /app/(authenticated)/sentimentals/page.tsx
    {
      path: '/sentimentals',
      handler: () => {
        router.push('/sentimentals');
      },
    },

    // Fax - /app/(authenticated)/fax/page.tsx
    {
      path: '/fax',
      handler: () => {
        router.push('/fax');
      },
    },

    // Lead Assignment - /app/(authenticated)/lead-assignment/page.tsx
    {
      path: '/lead-assignment',
      handler: () => {
        router.push('/lead-assignment');
      },
    },

    // Agent Leaderboard - /app/(authenticated)/leaderboard/page.tsx
    {
      path: '/leaderboard',
      handler: () => {
        router.push('/leaderboard');
      },
    },
    {
      path: '/leaderboard',
      handler: () => {
        router.push('/leaderboard');
      },
    },
  ];
}

/**
 * Create a deep link URL for the app
 */
export function createDeepLink(path: string, params?: Record<string, string>): string {
  const baseUrl = 'insuracentral://';
  let url = `${baseUrl}${path.startsWith('/') ? path.slice(1) : path}`;

  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  return url;
}

/**
 * Remove deep link listeners
 */
export async function removeDeepLinkListeners(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  await App.removeAllListeners();
  isListening = false;
  registeredRoutes = [];
  defaultHandler = null;
}

/**
 * Check if app was opened via deep link
 */
export async function getLaunchUrl(): Promise<string | null> {
  if (!isNativePlatform()) {
    return null;
  }

  try {
    const { url } = await App.getLaunchUrl() || {};
    return url || null;
  } catch (error) {
    console.error('Error getting launch URL:', error);
    return null;
  }
}
